import type { ApolloError } from '@apollo/client';
import * as React from 'react';
import {
  Button,
  CustomTooltipInfo,
  Heading,
  Spinner,
  Surface,
  Text,
} from '@quality24/design-system';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { v4 as uuid } from 'uuid';
import { CardComponentData, DateFilter } from '../../../../@types';
import DynamicCardSection from './DynamicCardSection';
import { parseDataValue } from '../../../../utils';

import styles from './CardContainer.module.scss';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  className?: string;
  loading?: boolean;
  error?: ApolloError;
  refetch: () => void;
  data: Record<string, Record<string, unknown>>;
  dateFilter: DateFilter;
  config: Omit<CardComponentData, 'type'>;
}

/**
 * <CardContainer> page
 */
export const CardContainer: React.FunctionComponent<Props> = ({
  className,
  data,
  dateFilter,
  loading,
  error,
  refetch,
  config: {
    className: configClassName,
    title,
    tooltipContent,
    options,
    subcomponents,
  },
  ...htmlElementProps
}) => {
  const parsedTitle = React.useMemo(
    () =>
      (title?.startsWith('$') || title?.startsWith('{{')) && loading
        ? ''
        : (parseDataValue(data, title || '', 'string') as string),
    [data, loading, title],
  );

  return (
    <div
      className={classNames(className, configClassName?.outer)}
      {...htmlElementProps}
    >
      <Surface
        className={classNames(styles['card-wrapper'], configClassName?.inner)}
        backgroundColor={options?.backgroundColor}
        color={options?.color}
      >
        <div className="d-flex flex-justify-between flex-items-start">
          {title && (
            <Heading
              as="h6"
              size="xl"
              weight="bold"
              className={classNames('pb-2', configClassName?.title)}
              title={parsedTitle}
            >
              {parsedTitle}
            </Heading>
          )}
          {tooltipContent && <CustomTooltipInfo info={tooltipContent} />}
        </div>

        {/* Show loading spinner */}
        {loading && (
          <Spinner type="light" size="sm" data-testid="card-spinner" />
        )}

        {!loading && error && (
          <div className="d-flex flex-column flex-items-center">
            <Text className="mb-2" size="sm2">
              Não foi possível carregar os dados.
            </Text>
            <Button
              variant="outline"
              icon={faRefresh}
              onClick={() => refetch()}
            >
              Recarregar
            </Button>
          </div>
        )}

        {!loading && !error && (
          <div className={classNames(styles.content, configClassName?.content)}>
            {subcomponents?.map((c) =>
              c.type === 'static' ? (
                <div
                  key={`${c.type}-${c.value}-${c.key}`}
                  className={c.className?.container}
                >
                  {c.value && (
                    <Text
                      key={`${c.type}-${c.value}`}
                      weight="bold"
                      className={c.className?.value}
                    >
                      {c.value}
                    </Text>
                  )}
                  {/* Render nested sections */}
                  {c.items?.map((item) =>
                    item.type === 'static' ? (
                      <div
                        key={`${item.type}-${item.value}-${item.key}`}
                        className={item.className?.container}
                      >
                        {item.value && (
                          <Text weight="bold" className={item.className?.value}>
                            {item.value}
                          </Text>
                        )}
                      </div>
                    ) : (
                      <DynamicCardSection
                        key={`${item.type}-${item.label || uuid()}-${
                          item.value
                        }`}
                        data={data}
                        className={item.className}
                        dateFilter={dateFilter}
                        config={item}
                      />
                    ),
                  )}
                </div>
              ) : (
                <DynamicCardSection
                  key={`${c.type}-${c.label || uuid()}-${c.value}`}
                  data={data}
                  className={c.className}
                  dateFilter={dateFilter}
                  config={c}
                />
              ),
            )}
          </div>
        )}
      </Surface>
    </div>
  );
};

export default CardContainer;
